import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'
import { PageLoader } from '@/components/ui/page-loader'
import { useEstimateStore } from '@/store/estimate-store'
import { estimatesService } from '@/services/estimates-service'
import { pricingService } from '@/services/pricing-service'
import { crewsService } from '@/services/crews-service'
import { companyService } from '@/services/company-service'
import { proposalsService } from '@/services/proposals-service'
import { lchService, type LchRow, type LchTenantCost } from '@/services/lch-service'
import { AddLineItemDialog } from '@/components/estimate/add-line-item-dialog'
import { WriteupPanel } from '@/components/estimate/writeup-panel'
import { SealCoatGradingPanel } from '@/components/estimate/sealcoat-grading-panel'
import { StripingEstimatePanel } from '@/components/estimate/striping-estimate-panel'
import { BidTotalsPanel } from '@/components/estimate/bid-totals-panel'
import { AssetsPanel } from '@/components/estimate/assets-panel'
import { InclusionsExclusionsPanel } from '@/components/estimate/inclusions-exclusions-panel'
import { AsphaltGradingPanel } from '@/components/estimate/asphalt-grading-panel'
import { MapPanel } from '@/components/estimate/map-panel'
import { UnitPricePanel } from '@/components/estimate/unit-price-panel'
import { ServiceSubcontractorsSection } from '@/components/estimate/service-subcontractors-section'
import { subcontractorCostBySection, sumLegacySubcontractors } from '@/lib/section-totals'
import { OptionsPanel } from '@/components/estimate/options-panel'
import { EditProjectDetailsModal } from '@/components/estimate/edit-project-details-modal'
import { FieldNotesPanel } from '@/components/estimate/field-notes-panel'
import { BackgroundMaterialSync } from '@/components/estimate/background-material-sync'
import { useOrphanedItemCleanup } from '@/hooks/useOrphanedItemCleanup'
import { createLineItemSaveQueue, type LineItemSaveQueue } from '@/lib/line-item-save-queue'
import { useEstimatePresence } from '@/hooks/use-estimate-presence'
import { ConcurrentEditorsBanner } from '@/components/estimate/concurrent-editors-banner'
import type { PricingItem, Crew, CreateLineItemPayload, EstimateStatus, LineItem } from '@/types'

const STATUS_STYLES: Record<EstimateStatus, { label: string; cssClass: string }> = {
  draft: { label: 'Draft', cssClass: 'in-progress' },
  submitted_for_approval: { label: 'Submitted for Approval', cssClass: 'ready' },
  approved: { label: 'Approved', cssClass: 'approved' },
  rejected: { label: 'Rejected', cssClass: 'rejected' },
}

const ADD_CATEGORY_OPTIONS = [
  { id: 'asphalt', label: 'Asphalt' },
  { id: 'concrete', label: 'Concrete' },
  { id: 'grading', label: 'Grading' },
  { id: 'seal-coat', label: 'Seal Coat' },
  { id: 'striping', label: 'Striping' },
  // No 'subcontractors' tab: subcontractors are a section inside each service tab.
  { id: 'options', label: 'Options' },
]

/** Service tabs — each ends with a Subcontractors section, after Miscellaneous. */
const SERVICE_TABS = new Set(['asphalt', 'concrete', 'grading', 'seal-coat', 'striping'])

const SECTION_PANELS = {
  mapping: { title: 'Mapping', desc: 'Draw and measure areas on the satellite map' },
  writeup: { title: 'Writeup', desc: 'Describe the scope of work for this estimate' },
  assets: { title: 'Assets', desc: 'Attach photos, documents, and files' },
  'inclusions-exclusions': { title: 'Inclusions/Exclusions', desc: 'Select inclusions and exclusions for this proposal' },
  'bid-totals': { title: 'Bid Totals', desc: 'Cost summary, markup, and final bid total' },
}

type SectionPanelKey = keyof typeof SECTION_PANELS
type SectionPanelType = SectionPanelKey | string

// How long to coalesce keystrokes on a line-item field before writing. Short enough
// that an edit is saved almost immediately after typing stops, long enough that typing
// a multi-digit quantity is a single write rather than one per digit.
const LINE_ITEM_SAVE_DEBOUNCE_MS = 500

export function EstimateBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    currentEstimate, lineItems, isLoading,
    setEstimate, updateEstimateFields, setLoading, setSaving,
    addLineItem, updateLineItem, removeLineItem, setLineItems,
    reset,
  } = useEstimateStore()

  const user = useAuthStore((s) => s.user)
  const userRole = user?.role || ''
  const isManagerOrAbove = userRole === 'MANAGER' || userRole === 'ADMIN' || userRole === 'SUPERADMIN'

  const [searchParams] = useSearchParams()

  const [pricingItems, setPricingItems] = useState<PricingItem[]>([])
  const [_crews, _setCrews] = useState<Crew[]>([])
  const [defaultFuelChargePercent, setDefaultFuelChargePercent] = useState(18)
  const [proposalTemplate, setProposalTemplate] = useState('modern')
  const [lchRows, setLchRows] = useState<LchRow[]>([])
  const [lchTenantCosts, setLchTenantCosts] = useState<LchTenantCost[]>([])
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingFieldsRef = useRef<Record<string, any>>({})
  // Set by BidTotalsPanel; calling it synchronously pushes the panel's pending (debounced)
  // markup-% save into the parent queue so it persists before we generate a proposal.
  const bidTotalsFlushRef = useRef<(() => void) | null>(null)
  // Line-item saves — see handleUpdateLineItem. Debounces keystrokes into one write per
  // row and serializes same-row writes so they can never be applied out of order.
  const lineItemQueueRef = useRef<LineItemSaveQueue<Partial<LineItem>> | null>(null)
  if (!lineItemQueueRef.current) {
    lineItemQueueRef.current = createLineItemSaveQueue<Partial<LineItem>>({
      // `id` is read at call time, so the queue survives it becoming available later.
      save: (itemId, patch) => estimatesService.updateLineItem(id!, itemId, patch),
      delayMs: LINE_ITEM_SAVE_DEBOUNCE_MS,
    })
  }
  const lineItemQueue = lineItemQueueRef.current
  const [activeTab, setActiveTab] = useState<SectionPanelType>((searchParams.get('tab') as SectionPanelType) || 'mapping')
  const [projectDetailsOpen, setProjectDetailsOpen] = useState(false)
  const [addCategoryOpen, setAddCategoryOpen] = useState(false)
  const [addedCategories, setAddedCategories] = useState<Set<string>>(new Set())
  const [lockedCategories, setLockedCategories] = useState<Set<string>>(new Set())
  const [_categoriesLoaded, _setCategoriesLoaded] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [bidTotalComputed, setBidTotalComputed] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [isApproved, setIsApproved] = useState(false)
  const [editDetailsOpen, setEditDetailsOpen] = useState(false)
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false)
  const [isUpdatingProposal, setIsUpdatingProposal] = useState(false)
  const [requireApproval, setRequireApproval] = useState(true)
  const [isCreatingProposal, setIsCreatingProposal] = useState(false)
  const [submitApprovalError, setSubmitApprovalError] = useState(false)
  const [returnToDraftError, setReturnToDraftError] = useState(false)
  const [isReturningToDraft, setIsReturningToDraft] = useState(false)
  const sectionWrapRef = useRef<HTMLDivElement>(null)
  // Surfaces the backend message verbatim when Approve fails so the reason
  // (network blip, role check, etc.) is visible instead of disappearing into
  // console.error. Cleared at the start of each Approve attempt.
  const [approveError, setApproveError] = useState<string | null>(null)

  const presenceOthers = useEstimatePresence(id)

  // Double-click on any .sc-input selects all text for quick overwrite
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.classList.contains('sc-input') && target instanceof HTMLInputElement) {
        target.select()
      }
    }
    document.addEventListener('dblclick', handler)
    return () => document.removeEventListener('dblclick', handler)
  }, [])

  // Load estimate data
  useEffect(() => {
    if (!id) return
    loadEstimate()
    loadPricingItems()
    loadCrews()
    loadCompanyDefaults()
    loadLchData()
    return () => { reset() }
  }, [id])

  // NOTE: Do NOT re-fetch LCH data on tab switch — BidTotalsPanel fetches its own data,
  // and re-fetching here would overwrite unsaved edits made in HaulDumpCalculator
  // before the debounced save completes, causing the "Cost Per Dump resets" bug.

  async function loadEstimate() {
    try {
      setLoading(true)
      const estimate = await estimatesService.getById(id!)
      // Auto-revert rejected estimates to draft when estimator opens them (not managers)
      if (estimate.status === 'rejected' && !isManagerOrAbove) {
        try {
          await estimatesService.updateStatus(id!, 'draft')
          estimate.status = 'draft'
        } catch {
          // Still show as draft locally even if API fails
          estimate.status = 'draft'
        }
      }
      setEstimate(estimate)

      // Derive locked category tabs from wizard projectTypes
      // Normalize to lowercase kebab-case (opportunities may store "Asphalt", "Seal Coat")
      const locked = new Set<string>()
      try {
        const ptParsed = JSON.parse(estimate.projectTypes || '[]')
        if (Array.isArray(ptParsed)) {
          const normalized = ptParsed.map((t: string) => t.toLowerCase().replace(/\s+/g, '-'))
          const ptSet = new Set(normalized)
          if (ptSet.has('asphalt')) locked.add('asphalt')
          if (ptSet.has('concrete')) locked.add('concrete')
          if (ptSet.has('grading')) locked.add('grading')
          if (ptSet.has('seal-coat')) locked.add('seal-coat')
          if (ptSet.has('striping')) locked.add('striping')
        }
      } catch { /* ignore */ }
      setLockedCategories(locked)

      // Restore persisted added categories and merge locked ones in
      // Filter to only valid estimate categories (old estimates may have work type names)
      // 'subcontractors' is deliberately absent: it was a standalone tab and is now a
      // section inside each service tab. Older estimates that saved it simply don't
      // show the tab any more; their entries stay in subcontractorsData.
      const VALID_CATS = new Set(['asphalt', 'concrete', 'grading', 'seal-coat', 'striping', 'options'])
      const added = new Set(locked)
      try {
        const parsed = JSON.parse(estimate.addedCategories || '[]')
        if (Array.isArray(parsed)) {
          parsed.forEach((c: string) => {
            const norm = c.toLowerCase().replace(/\s+/g, '-')
            if (VALID_CATS.has(norm)) added.add(norm)
          })
        }
      } catch { /* ignore parse errors */ }
      setAddedCategories(added)
      _setCategoriesLoaded(true)
    } catch {
      // Could not load estimate — redirect to list
      navigate('/estimates')
      return
    } finally {
      setLoading(false)
    }
  }

  async function loadPricingItems() {
    try {
      const items = await pricingService.list()
      setPricingItems(items)
    } catch {
      setPricingItems([])
    }
  }

  async function loadCrews() {
    try {
      const data = await crewsService.list()
      _setCrews(data)
    } catch {
      _setCrews([])
    }
  }

  async function loadCompanyDefaults() {
    try {
      const settings = await companyService.getSettings()
      if (settings.defaultFuelChargePercent !== undefined && settings.defaultFuelChargePercent !== null) {
        setDefaultFuelChargePercent(settings.defaultFuelChargePercent)
      }
      if (settings.proposalTemplate) {
        setProposalTemplate(settings.proposalTemplate)
      }
      if (settings.requireApproval !== undefined) {
        setRequireApproval(settings.requireApproval)
      }
      // Global markup removed — per-row markup in bid totals panel is now the only markup
    } catch {
      // keep default
    }
  }

  async function loadLchData() {
    if (!id) return
    try {
      const [costs, data] = await Promise.all([
        lchService.listTenantCosts(),
        lchService.getEstimateLchData(id),
      ])
      if (costs) setLchTenantCosts(costs)
      if (data) {
        const rows = Array.isArray(data) ? data : (typeof data === 'string' ? (() => { try { return JSON.parse(data as string) } catch { return [] } })() : [])
        setLchRows(prev => prev.length === 0 ? rows : prev)
      }
    } catch {
      // keep empty
    }
  }



  // Auto-save handler with debounce
  const flushPendingFields = useCallback(async () => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null }
    const fields = pendingFieldsRef.current
    pendingFieldsRef.current = {}
    if (Object.keys(fields).length === 0 || !id) return
    try {
      setSaving(true)
      await estimatesService.update(id, fields)
    } catch {
      // Silently fail for demo
    } finally {
      setSaving(false)
    }
  }, [id])

  const debouncedSave = useCallback((field: string, value: any) => {
    pendingFieldsRef.current[field] = value
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(async () => {
      saveTimerRef.current = null
      const fields = pendingFieldsRef.current
      pendingFieldsRef.current = {}
      if (Object.keys(fields).length === 0 || !id) return
      try {
        setSaving(true)
        await estimatesService.update(id, fields)
      } catch {
        // Silently fail for demo
      } finally {
        setSaving(false)
      }
    }, 800)
  }, [id])

  // Handle project info updates
  // function handleProjectUpdate(field: string, value: string) {
  //   if (!currentEstimate) return
  //   updateEstimateFields({ [field]: value })
  //   debouncedSave(field, value)
  // }

  // Handle add line item (used by both dialog and section pickers)
  async function handleAddLineItem(payload: CreateLineItemPayload) {
    try {
      const item = await estimatesService.addLineItem(id!, payload)
      // Merge payload fields onto server response as fallback
      addLineItem({ ...item, ...payload, id: item.id, lineTotal: item.lineTotal })
    } catch {
      // Fallback — add locally so the UI still works
      addLineItem({
        id: `li-${Date.now()}`,
        estimateId: id!,
        ...payload,
        lineTotal: payload.quantity * payload.rate,
        sortOrder: lineItems.length,
        createdAt: new Date().toISOString(),
      })
    }
  }

  // Handle add line item from dialog (closes dialog after)
  async function handleAddLineItemFromDialog(payload: CreateLineItemPayload) {
    await handleAddLineItem(payload)
    setAddDialogOpen(false)
  }

  // A debounced edit is only in memory until it fires, so push it out on the two ways
  // the page can disappear: unmount (navigating away) and tab close/refresh.
  useEffect(() => {
    const onBeforeUnload = () => {
      for (const [itemId] of lineItemQueue.pending()) lineItemQueue.saveNow(itemId)
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      onBeforeUnload()
    }
  }, [lineItemQueue])

  // Persist every pending line-item edit and wait for the writes to land. Called before
  // anything that reads line items back off the server (preview, submit, approve,
  // proposal generation) — the same guard the markup/debounced-field saves already use.
  const flushPendingLineItems = useCallback(() => lineItemQueue.flush(), [lineItemQueue])

  // Handle update line item (supports equipment fuel charge + labor overtime)
  //
  // This used to fire one PUT per keystroke with no debounce and no ordering guarantee.
  // Typing "18" sent `quantity=1` then `quantity=18`; if those landed out of order the
  // row persisted as 1 while the screen still showed 18 from optimistic state — so the
  // generated PDF showed a quantity missing its second digit. Clicking Preview inside
  // the same moment had the same effect, since line items were not part of any flush.
  //
  // The store is still updated immediately (typing stays responsive); only the network
  // write is debounced and serialized per row.
  //
  // Stays `async` so it keeps returning a promise for the panels that type
  // `onUpdateItem` that way — it just no longer awaits the request.
  async function handleUpdateLineItem(itemId: string, data: Partial<LineItem>) {
    const existing = lineItems.find((li) => li.id === itemId)
    if (!existing) return

    // Use provided lineTotal if available (sections pre-calculate), otherwise simple calc
    const lineTotal = data.lineTotal ?? ((data.quantity ?? existing.quantity) * (data.rate ?? existing.rate))
    updateLineItem(itemId, { ...data, lineTotal })

    lineItemQueue.queue(itemId, data)
  }

  // Handle delete line item
  async function handleDeleteLineItem(itemId: string) {
    // Drop any debounced edit for this row first — letting it fire after the DELETE
    // would PUT a row that no longer exists.
    lineItemQueue.cancel(itemId)

    removeLineItem(itemId)
    try {
      // Wait for any in-flight write for this row so the DELETE can't overtake it.
      await lineItemQueue.settled(itemId)
      await estimatesService.deleteLineItem(id!, itemId)
    } catch {
      // Already removed optimistically
    }
  }

  // Handle drag-to-reorder from the Unit Price panel.
  // `orderedIds` is EVERY line item id in its new global order — the reorder
  // endpoint rewrites sortOrder to the index of each id it receives, so sending
  // a partial list would renumber those rows into another section's range.
  async function handleReorderLineItems(orderedIds: string[]) {
    const byId = new Map(lineItems.map((li) => [li.id, li]))
    const reordered = orderedIds
      .map((itemId, index) => {
        const item = byId.get(itemId)
        return item ? { ...item, sortOrder: index } : null
      })
      .filter((li): li is LineItem => li !== null)

    // Bail rather than persist a partial order if anything didn't resolve.
    if (reordered.length !== lineItems.length) return

    const previous = lineItems
    setLineItems(reordered) // optimistic

    try {
      await estimatesService.reorderLineItems(id!, orderedIds)
    } catch {
      setLineItems(previous) // roll back so the UI matches what was saved
    }
  }

  // When an H&D row is deleted, uncheck the corresponding writeup row
  const handleWriteupRowDisable = useCallback((writeupRowId: string) => {
    const raw = currentEstimate?.scopeData
    if (!raw) return
    try {
      const groups = JSON.parse(raw)
      let changed = false
      for (const group of groups) {
        const allRows = [...(group.rows || []), ...(group.sections || []).flatMap((s: { rows?: unknown[] }) => s.rows || [])]
        for (const row of allRows) {
          if (row.id === writeupRowId && row.enabled) {
            row.enabled = false
            // Clear field values (same behavior as WriteupPanel's toggleRow)
            if (Array.isArray(row.content)) {
              for (const c of row.content) {
                if (typeof c === 'object' && c.value !== undefined) c.value = ''
              }
            }
            changed = true
          }
        }
      }
      if (changed) {
        const updated = JSON.stringify(groups)
        updateEstimateFields({ scopeData: updated })
        estimatesService.update(id!, { scopeData: updated }).catch(() => {})
      }
    } catch {
      // Invalid JSON — skip
    }
  }, [currentEstimate?.scopeData, id, updateEstimateFields])

  // When H&D area/depth changes, push the new value back into the writeup scopeData
  const handleWriteupFieldUpdate = useCallback((writeupRowId: string, fieldName: string, value: number) => {
    const raw = currentEstimate?.scopeData
    if (!raw) return
    try {
      const groups = JSON.parse(raw)
      let changed = false
      for (const group of groups) {
        const allRows = [...(group.rows || []), ...(group.sections || []).flatMap((s: { rows?: unknown[] }) => s.rows || [])]
        for (const row of allRows) {
          if (row.id === writeupRowId && Array.isArray(row.content)) {
            for (const c of row.content) {
              if (typeof c === 'object' && c.placeholder === fieldName) {
                const newVal = value > 0 ? String(value) : ''
                if (c.value !== newVal) {
                  c.value = newVal
                  changed = true
                }
              }
            }
          }
        }
      }
      if (changed) {
        const updated = JSON.stringify(groups)
        updateEstimateFields({ scopeData: updated })
        estimatesService.update(id!, { scopeData: updated }).catch(() => {})
      }
    } catch {
      // Invalid JSON — skip
    }
  }, [currentEstimate?.scopeData, id, updateEstimateFields])

  // Keep lchRows current whenever HaulDumpCalculator saves — prevents stale
  // initialLchData being passed on remount (which would reset user-edited values).
  const handleLchDataChange = useCallback((sectionRows: LchRow[]) => {
    setLchRows(prev => {
      // Determine which section these rows belong to (all rows share the same section)
      const section = sectionRows[0]?.section
      if (!section) return [...sectionRows]
      // Replace rows for this section, keep all other sections intact
      const otherRows = prev.filter(r => r.section !== section)
      return [...otherRows, ...sectionRows]
    })
  }, [])

  // ── Parent-level cleanup: remove orphaned formula-driven items ──
  // Runs whenever scopeData changes, regardless of which tab is active.
  // Fixes the issue where unchecking writeup rows while on the Writeup tab
  // left orphaned materials and H&D rows in inactive section tabs.
  useOrphanedItemCleanup({
    estimateId: id,
    scopeData: currentEstimate?.scopeData,
    lineItems,
    pricingItems,
    onDeleteItem: handleDeleteLineItem,
  })

  // Handle add note
  // async function handleAddNote(content: string) {
  //   try {
  //     const note = await estimatesService.addNote(id!, content)
  //     addNote(note)
  //   } catch {
  //     // Optimistic add for demo
  //     const mockNote: EstimateNote = {
  //       id: `note-${Date.now()}`,
  //       estimateId: id!,
  //       authorId: 'current-user',
  //       author: { id: 'current-user', displayName: 'You' },
  //       content,
  //       createdAt: new Date().toISOString(),
  //     }
  //     addNote(mockNote)
  //   }
  // }

  // Submit Revision for Approval — routes the revised estimate back through the approval
  // queue. On approval, the existing proposal updates in place (same proposal #, bumped
  // revisionNumber). Logic lives server-side in proposal-service.createProposal — it
  // detects the estimate's revisionProposalId and swaps onto the existing proposal.
  async function handleUpdateProposal() {
    if (!currentEstimate?.revisionProposalId || isUpdatingProposal) return
    setIsUpdatingProposal(true)
    try {
      await flushPendingFields()
      await flushPendingLineItems()
      const fieldsToFlush: Record<string, any> = {}
      if (currentEstimate.scopeData) fieldsToFlush.scopeData = currentEstimate.scopeData
      if (currentEstimate.scopeOfWork) fieldsToFlush.scopeOfWork = currentEstimate.scopeOfWork
      if (currentEstimate.addedCategories) fieldsToFlush.addedCategories = currentEstimate.addedCategories
      if (currentEstimate.markupData) fieldsToFlush.markupData = currentEstimate.markupData
      if (currentEstimate.discountType) fieldsToFlush.discountType = currentEstimate.discountType
      if (currentEstimate.discountValue !== undefined) fieldsToFlush.discountValue = currentEstimate.discountValue
      if (currentEstimate.selectedInclusionIds) fieldsToFlush.selectedInclusionIds = currentEstimate.selectedInclusionIds
      if (currentEstimate.selectedExclusionIds) fieldsToFlush.selectedExclusionIds = currentEstimate.selectedExclusionIds
      if (currentEstimate.projectConditions) fieldsToFlush.projectConditions = currentEstimate.projectConditions
      if (currentEstimate.additionalNotes) fieldsToFlush.additionalNotes = currentEstimate.additionalNotes
      if (currentEstimate.subcontractorsData) fieldsToFlush.subcontractorsData = currentEstimate.subcontractorsData
      if (currentEstimate.priceValidThrough) fieldsToFlush.priceValidThrough = currentEstimate.priceValidThrough
      if (bidTotalComputed !== null && bidTotalComputed > 0) fieldsToFlush.total = bidTotalComputed
      if (Object.keys(fieldsToFlush).length > 0) {
        await estimatesService.update(id!, fieldsToFlush)
      }
      await new Promise(r => setTimeout(r, 300))
      await estimatesService.updateStatus(id!, 'submitted_for_approval')
      navigate('/proposals')
    } catch (err) {
      console.error('Failed to submit revision for approval:', err)
      alert('Failed to submit revision for approval. Please try again.')
    } finally {
      setIsUpdatingProposal(false)
    }
  }

  // Handle cancel revision
  async function handleCancelRevision() {
    if (!currentEstimate?.revisionProposalId) return
    if (!confirm('Are you sure you want to cancel this revision? All changes will be lost.')) return
    try {
      await proposalsService.cancelRevision(currentEstimate.revisionProposalId)
      navigate('/proposals')
    } catch (err) {
      console.error('Failed to cancel revision:', err)
    }
  }

  // Handle submit for approval — optimistic: flip UI immediately, revert on failure
  async function handleSubmit() {
    setSubmitApprovalError(false)
    updateEstimateFields({ status: 'submitted_for_approval' })
    try {
      await flushPendingFields()
      await flushPendingLineItems()
      const pendingFields: Record<string, any> = {}
      if (currentEstimate?.markupData) pendingFields.markupData = currentEstimate.markupData
      if (currentEstimate?.scopeData) pendingFields.scopeData = currentEstimate.scopeData
      if (currentEstimate?.subcontractorsData) pendingFields.subcontractorsData = currentEstimate.subcontractorsData
      if (currentEstimate?.projectConditions) pendingFields.projectConditions = currentEstimate.projectConditions
      if (currentEstimate?.additionalNotes) pendingFields.additionalNotes = currentEstimate.additionalNotes
      if (bidTotalComputed !== null && bidTotalComputed > 0) pendingFields.total = bidTotalComputed
      if (id && Object.keys(pendingFields).length > 0) {
        await estimatesService.update(id, pendingFields)
      }
      await estimatesService.updateStatus(id!, 'submitted_for_approval')
    } catch (err) {
      console.error('Failed to submit for approval:', err)
      updateEstimateFields({ status: 'draft' })
      setSubmitApprovalError(true)
    }
  }

  /*
   * While an estimate awaits approval the backend rejects estimator writes with
   * a 409 (blockEstimatorWritesWhileSubmitted). Mirror that in the UI so the
   * estimator sees a locked panel rather than typing into fields whose saves
   * silently fail. Managers/admins are the reviewers and are never locked.
   *
   * `inert` is set imperatively because React 18 does not forward it as a JSX
   * prop. It blocks clicks AND keyboard focus, which `pointer-events: none`
   * alone does not.
   */
  const isLockedPendingApproval =
    currentEstimate?.status === 'submitted_for_approval' && !isManagerOrAbove

  useEffect(() => {
    const el = sectionWrapRef.current
    if (!el) return
    if (isLockedPendingApproval) el.setAttribute('inert', '')
    else el.removeAttribute('inert')
  }, [isLockedPendingApproval, activeTab])

  /**
   * Pull a submitted estimate back out of the approval queue so the estimator
   * can edit it and resubmit. The backend already allows ESTIMATOR -> draft
   * (see the role guard in estimates-controller.ts); this only exposes it.
   */
  async function handleReturnToDraft() {
    setReturnToDraftError(false)
    setIsReturningToDraft(true)
    const previousStatus = currentEstimate?.status
    updateEstimateFields({ status: 'draft' })
    try {
      await estimatesService.updateStatus(id!, 'draft')
    } catch (err) {
      console.error('Failed to return estimate to draft:', err)
      if (previousStatus) updateEstimateFields({ status: previousStatus })
      setReturnToDraftError(true)
    } finally {
      setIsReturningToDraft(false)
    }
  }

  // Create Proposal directly (managers always, estimators when requireApproval is off)
  async function handleCreateProposal() {
    if (!id) return
    setIsCreatingProposal(true)
    try {
      // Force the Bid Totals panel's pending (debounced) markup save into the queue, then
      // flush — so a just-entered markup % is persisted BEFORE the proposal is generated.
      // (Without this, generating within ~1s of a markup edit prices the proposal off the
      // stale, not-yet-saved markup — the reported bug.)
      bidTotalsFlushRef.current?.()
      await flushPendingFields()
      await flushPendingLineItems()
      if (bidTotalComputed !== null && bidTotalComputed > 0) {
        try { await estimatesService.update(id, { total: bidTotalComputed }) } catch { /* best-effort */ }
      }
      const proposal = await proposalsService.create(id)
      updateEstimateFields({ status: 'approved' })
      setIsApproved(true)
      navigate(`/proposals/${proposal.id}`)
    } catch (err) {
      console.error('Failed to create proposal:', err)
    } finally {
      setIsCreatingProposal(false)
    }
  }

  // Handle approve estimate (manager action)
  async function handleApprove() {
    setApproveError(null)
    try {
      // Approving triggers proposal creation on the backend, so flush any pending markup
      // edit first — otherwise the generated proposal prices off stale markup.
      bidTotalsFlushRef.current?.()
      await flushPendingFields()
      await flushPendingLineItems()
      await estimatesService.updateStatus(id!, 'approved')
      updateEstimateFields({ status: 'approved' })
      setIsApproved(true)
    } catch (err: any) {
      console.error('Failed to approve estimate:', err)
      setApproveError(err?.response?.data?.message || 'Failed to approve — please try again')
    }
  }

  // Handle reject estimate (manager action) — opens modal
  function handleRejectClick() {
    setRejectReason('')
    setRejectModalOpen(true)
  }

  // Confirm rejection with reason
  async function handleRejectConfirm() {
    const reason = rejectReason.trim()
    if (!reason) return
    try {
      const updated = await estimatesService.updateStatus(id!, 'rejected', reason)
      updateEstimateFields({
        status: 'rejected',
        rejectionReason: updated.rejectionReason ?? reason,
        rejectedAt: updated.rejectedAt ?? null,
        rejectedByUser: updated.rejectedByUser ?? null,
      })
      setRejectModalOpen(false)
    } catch (err) {
      console.error('Failed to reject estimate:', err)
      // Do NOT fake a local 'rejected' state on failure — leave the modal open so
      // the manager can retry. (Repo rule: never update state in a catch block.)
    }
  }

  // Preview Proposal — always opens in preview mode (no app shell, no Send)
  // Flushes any pending debounced saves before navigating so the proposal
  // preview (which re-fetches from the API) sees the latest data.
  async function handlePreviewProposal() {
    // Flush pending markup + debounced saves before opening preview so the re-fetched
    // preview reflects the latest markup %.
    bidTotalsFlushRef.current?.()
    await flushPendingFields()
    await flushPendingLineItems()

    // Build an object of all fields that might have unsaved changes
    const pendingFields: Record<string, any> = {}
    if (currentEstimate?.scopeData) pendingFields.scopeData = currentEstimate.scopeData
    if (currentEstimate?.markupData) pendingFields.markupData = currentEstimate.markupData
    if (currentEstimate?.subcontractorsData) pendingFields.subcontractorsData = currentEstimate.subcontractorsData
    if (currentEstimate?.projectConditions) pendingFields.projectConditions = currentEstimate.projectConditions
    if (currentEstimate?.additionalNotes) pendingFields.additionalNotes = currentEstimate.additionalNotes
    if (currentEstimate?.priceValidThrough) pendingFields.priceValidThrough = currentEstimate.priceValidThrough
    // Always persist the Bid Totals Panel's markup-applied total so the preview sees it
    if (bidTotalComputed !== null && bidTotalComputed > 0) pendingFields.total = bidTotalComputed

    // Flush to API so the proposal preview has the latest data
    if (id && Object.keys(pendingFields).length > 0) {
      try { await estimatesService.update(id, pendingFields) } catch { /* best-effort */ }
    }

    const params = new URLSearchParams()
    params.set('mode', 'preview')
    params.set('from', `/estimates/${id}`)
    params.set('template', proposalTemplate)
    navigate(`/proposals/preview?${params.toString()}`)
  }

  // Handle add crew as line items
  // async function handleAddCrewAsLineItems(crewId: string) {
  //   const crew = crews.find((c) => c.id === crewId)
  //   if (!crew) return
  //   for (const member of crew.members) {
  //     await handleAddLineItem({
  //       description: member.role || `Crew: ${crew.name}`,
  //       category: 'crew',
  //       quantity: member.quantity,
  //       unit: 'hr',
  //       rate: member.rate,
  //     })
  //   }
  // }

  // Persist added categories to the estimate.
  // Sync the Zustand store too — handleUpdateProposal reads currentEstimate.addedCategories
  // when flushing a revision for approval, so leaving the store stale would let an old value
  // overwrite the freshly-debounced one and drop categories the user just added (e.g. Options).
  function persistCategories(cats: Set<string>) {
    setAddedCategories(cats)
    const json = JSON.stringify(Array.from(cats))
    updateEstimateFields({ addedCategories: json })
    debouncedSave('addedCategories', json)
  }

  // Handle add category toggle
  function handleToggleCategory(categoryId: string) {
    // Locked categories (from wizard) can't be toggled off
    if (lockedCategories.has(categoryId) && addedCategories.has(categoryId)) return
    const newCategories = new Set(addedCategories)
    if (newCategories.has(categoryId)) {
      newCategories.delete(categoryId)
    } else {
      newCategories.add(categoryId)
    }
    persistCategories(newCategories)
  }



  if (isLoading) {
    return (
      <PageLoader />
    )
  }

  if (!currentEstimate) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-slate-500 font-medium">Estimate not found</p>
          <button onClick={() => navigate('/estimates')} className="mt-3 text-sm text-[#0074F4] font-medium">
            Back to Estimates
          </button>
        </div>
      </div>
    )
  }

  const statusStyle = STATUS_STYLES[currentEstimate.status] || STATUS_STYLES.draft
  const isUnitPricing = !!currentEstimate.isUnitPricing
  const relevantItems = isUnitPricing
    ? lineItems.filter((item) => item.section === 'unit-price')
    : lineItems
  const lineItemSubtotal = relevantItems.reduce((sum, item) => sum + item.lineTotal, 0)
  const lchTotal = lchRows.reduce((sum, r) => sum + (r.totalHaulDumpCost || 0), 0)
  // Pre-markup fallback (the Final Bid Totals panel reports the real total once it
  // mounts). Legacy entries count at cost + their own markup + bonds; service
  // entries at cost + bonds, since their markup lives in Final Bid Totals.
  const subcontractorRows = currentEstimate?.subcontractorsData || []
  const subcontractorTotal = sumLegacySubcontractors(subcontractorRows)
    + Object.values(subcontractorCostBySection(subcontractorRows)).reduce((s, v) => s + v, 0)
  const subtotal = lineItemSubtotal + lchTotal + subcontractorTotal
  // Global markup removed — per-row markup is handled inside BidTotalsPanel
  const total = subtotal

  // Discount calculation
  const discountType = (currentEstimate.discountType as 'percentage' | 'flat') || 'percentage'
  const discountValue = currentEstimate.discountValue || 0
  const showDiscountOnProposal = currentEstimate.showDiscountOnProposal || false
  const discountAmount = discountType === 'percentage'
    ? total * (discountValue / 100)
    : discountValue
  // Use bid-totals-panel computed total (includes per-row markup + discount) when available
  const finalTotal = bidTotalComputed !== null ? bidTotalComputed : (discountValue > 0 ? Math.max(0, total - discountAmount) : total)

  // Parse project types from wizard to filter visible tabs
  let projectTypesSet = new Set<string>()
  try {
    const parsed = JSON.parse(currentEstimate.projectTypes || '[]')
    if (Array.isArray(parsed)) projectTypesSet = new Set(parsed)
  } catch {
    // Default to showing all tabs if no projectTypes
  }
  // If no project types set (legacy/manual estimates), show all tabs
  if (projectTypesSet.size === 0) {
    projectTypesSet = new Set(['asphalt', 'concrete', 'grading', 'seal-coat', 'striping'])
  }

  return (
    <div className="est-content-pad">
      {/* Someone else is in this estimate right now. Presence was already shown as
          small avatars in the header, but they were missed often enough that two
          people edited the same bid and overwrote each other, so it is spelled out
          across the top as well. */}
      <ConcurrentEditorsBanner others={presenceOthers} />

      {/* Revision Banner */}
      {currentEstimate.isRevision && currentEstimate.revisionProposalId && (
        <div className="rev-banner">
          <div className="rev-banner-icon">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
          <div className="rev-banner-text">
            <strong>Revision Mode</strong> — Make your changes, then click "Submit Revision for Approval". Once a manager approves, the existing proposal will update in place (same proposal number, link, and original estimator).
          </div>
          <button className="rev-banner-cancel" onClick={handleCancelRevision}>
            Cancel Revision
          </button>
        </div>
      )}

      {/* Rejection Banner — persistent reason after a manager rejects */}
      {currentEstimate?.rejectionReason ? (
        <div className="rej-banner" role="alert">
          <span className="rej-banner-title">
            Rejected{currentEstimate.rejectedByUser?.displayName ? ` by ${currentEstimate.rejectedByUser.displayName}` : ''}
            {currentEstimate.rejectedAt ? ` on ${new Date(currentEstimate.rejectedAt).toLocaleDateString()}` : ''}
          </span>
          <span className="rej-banner-reason">{currentEstimate.rejectionReason}</span>
        </div>
      ) : null}

      {/* Awaiting-approval lock — explains why the panels below are read-only */}
      {isLockedPendingApproval && (
        <div className="est-lock-banner" role="status">
          <span className="est-lock-banner-title">Awaiting approval</span>
          <span className="est-lock-banner-text">
            This estimate is locked while your manager reviews it. Use{' '}
            <strong>Return to Draft</strong> at the bottom of the page to make changes.
          </span>
        </div>
      )}

      {/* Page Header — matches preview.html */}
      <div>
        <div className="est-page-head">
          <div className="est-title-group">
            <h1 className="est-title">
              {currentEstimate.title}
              <button
                className="est-edit-btn"
                onClick={() => setEditDetailsOpen(true)}
                title="Edit project details"
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                  <path d="m15 5 4 4" />
                </svg>
              </button>
            </h1>
            <span className={`est-status ${statusStyle.cssClass}`}>{statusStyle.label}</span>
          </div>
          <div className="est-actions">
            {presenceOthers.length > 0 && (
              <div className="est-presence" title={presenceOthers.map(u => u.displayName).join(', ') + ' also viewing'}>
                {presenceOthers.slice(0, 3).map((u) => (
                  <span key={u.userId} className="est-presence-avatar" title={u.displayName}>
                    {u.displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                ))}
                {presenceOthers.length > 3 && (
                  <span className="est-presence-avatar est-presence-overflow">+{presenceOthers.length - 3}</span>
                )}
                <span className="est-presence-label">
                  {presenceOthers.length === 1 ? `${presenceOthers[0].displayName} is also viewing` : `${presenceOthers.length} others viewing`}
                </span>
              </div>
            )}
            <button
              onClick={() => setProjectDetailsOpen(!projectDetailsOpen)}
              className={`proj-details-link ${projectDetailsOpen ? 'open' : ''}`}
            >
              <span>Project Details</span>
              <svg
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <button
              className="est-notes-btn"
              onClick={() => setNotesDrawerOpen(true)}
              aria-label="Project Notes — internal, staff only"
            >
              <span className="enb-label">Project Notes</span>
            </button>
          </div>
        </div>

        {/* Project Info Accordion */}
        <div className={`proj-info-accordion ${projectDetailsOpen ? 'open' : ''}`}>
          <div className="proj-info est-proj-info">
            <div className="proj-info-col">
              <div className="proj-info-label">Client</div>
              <div className="proj-info-value">
                <div className="font-medium">{currentEstimate.clientName}</div>
                {currentEstimate.clientEmail && (
                  <a href={`mailto:${currentEstimate.clientEmail}`}>{currentEstimate.clientEmail}</a>
                )}
                {currentEstimate.clientPhone && <div>{currentEstimate.clientPhone}</div>}
              </div>
            </div>
            <div className="proj-info-col">
              <div className="proj-info-label">Supervisor</div>
              <div className="proj-info-value">
                <div className="font-medium">{currentEstimate.supervisorName || '—'}</div>
                {currentEstimate.supervisorEmail && (
                  <a href={`mailto:${currentEstimate.supervisorEmail}`}>{currentEstimate.supervisorEmail}</a>
                )}
                {currentEstimate.supervisorPhone && <div>{currentEstimate.supervisorPhone}</div>}
              </div>
            </div>
            <div className="proj-info-col">
              <div className="proj-info-label">Project Address</div>
              <div className="proj-info-value">
                {currentEstimate.jobAddress && <div>{currentEstimate.jobAddress}</div>}
                {(currentEstimate.jobCity || currentEstimate.jobState || currentEstimate.jobZip) && (
                  <div>
                    {currentEstimate.jobCity}, {currentEstimate.jobState} {currentEstimate.jobZip}
                  </div>
                )}
              </div>
            </div>
            <div className="proj-info-col">
              <div className="proj-info-label">Public Works</div>
              <div className="proj-info-value">{currentEstimate.isPublicWorks ? 'Yes' : 'No'}</div>
            </div>
            {currentEstimate.isUnitPricing && (
              <div className="proj-info-col">
                <div className="proj-info-label">Unit Pricing</div>
                <div className="proj-info-value">Yes</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="est-tabs-wrap">
          <div className="section-tabs">
            {/* Mapping tab — always shown */}
            <button
              onClick={() => setActiveTab('mapping')}
              className={`section-tab ${activeTab === 'mapping' ? 'active' : ''}`}
            >
              Mapping
            </button>

            {/* Unit Pricing mode: show only Mapping, Unit Price, Assets */}
            {currentEstimate?.isUnitPricing ? (
              <>
                <button
                  onClick={() => setActiveTab('assets')}
                  className={`section-tab ${activeTab === 'assets' ? 'active' : ''}`}
                >
                  Assets
                </button>
                <button
                  onClick={() => setActiveTab('unit-price')}
                  className={`section-tab ${activeTab === 'unit-price' ? 'active' : ''}`}
                >
                  Unit Price
                </button>
              </>
            ) : (
              <>
                {/* Writeup tab */}
                <button
                  onClick={() => setActiveTab('writeup')}
                  className={`section-tab ${activeTab === 'writeup' ? 'active' : ''}`}
                >
                  Writeup
                </button>

                {/* Added category tabs (alphabetical) */}
                {Array.from(addedCategories).sort((a, b) => a.localeCompare(b)).map((categoryId) => {
                  const option = ADD_CATEGORY_OPTIONS.find((opt) => opt.id === categoryId)
                  return (
                    <button
                      key={categoryId}
                      onClick={() => setActiveTab(categoryId as SectionPanelType)}
                      className={`section-tab added ${activeTab === categoryId ? 'active' : ''}`}
                    >
                      {option?.label}
                    </button>
                  )
                })}

                {/* Assets tab */}
                <button
                  onClick={() => setActiveTab('assets')}
                  className={`section-tab ${activeTab === 'assets' ? 'active' : ''}`}
                >
                  Assets
                </button>

                {/* Inclusions/Exclusions tab */}
                <button
                  onClick={() => setActiveTab('inclusions-exclusions')}
                  className={`section-tab ${activeTab === 'inclusions-exclusions' ? 'active' : ''}`}
                >
                  Inclusions/Exclusions
                </button>

                {/* Bid Totals */}
                <button
                  onClick={() => setActiveTab('bid-totals')}
                  className={`section-tab ${activeTab === 'bid-totals' ? 'active' : ''}`}
                >
                  Bid Totals
                </button>

                {/* Add Category Button — pushed to far right */}
                <div className="add-section-wrapper" style={{ marginLeft: 'auto' }}>
                  <button
                    onClick={() => setAddCategoryOpen(!addCategoryOpen)}
                    className="add-section-btn"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <line x1="12" y1="5" x2="12" y2="19" strokeWidth="2.5" />
                      <line x1="5" y1="12" x2="19" y2="12" strokeWidth="2.5" />
                    </svg>
                    Add Category
                  </button>

                  {/* Add Category Dropdown */}
                  {addCategoryOpen && (
                    <div className="add-section-dropdown open">
                      {ADD_CATEGORY_OPTIONS.map((option) => {
                        const isLocked = lockedCategories.has(option.id)
                        return (
                          <button
                            key={option.id}
                            onClick={() => {
                              if (isLocked && addedCategories.has(option.id)) return
                              handleToggleCategory(option.id)
                            }}
                            className={`add-section-dropdown-item ${addedCategories.has(option.id) ? 'checked' : ''} ${isLocked ? 'locked' : ''}`}
                          >
                            <div className="check-icon">
                              {addedCategories.has(option.id) && (
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '100%', height: '100%' }}>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span>{option.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

      {/* Section Content — `inert` while locked (see the effect above), so the
          panels can't be edited into a 409 from the server-side lock. */}
      <div
        ref={sectionWrapRef}
        className={`est-section-wrap${isLockedPendingApproval ? ' is-locked' : ''}`}
      >
        {/* Unit Price Panel (only in unit pricing mode) */}
        {activeTab === 'unit-price' && currentEstimate?.isUnitPricing && (
          <UnitPricePanel
            estimateId={id || 'demo-1'}
            lineItems={lineItems}
            onAddItem={handleAddLineItem}
            onUpdateItem={handleUpdateLineItem}
            onDeleteItem={handleDeleteLineItem}
            onReorderItems={handleReorderLineItems}
          />
        )}

        {/* Writeup Panel */}
        {activeTab === 'writeup' && (
          <WriteupPanel
            estimateId={id || 'demo-1'}
            projectTypes={Array.from(addedCategories).filter(c => ['asphalt', 'concrete', 'grading', 'seal-coat', 'striping'].includes(c))}
            initialScopeData={currentEstimate?.scopeData}
            onScopeChange={(scopeData) => {
              updateEstimateFields({ scopeData });
            }}
          />
        )}

        {/* Inclusions / Exclusions Panel */}
        {activeTab === 'inclusions-exclusions' && (
          <InclusionsExclusionsPanel
            estimateId={id || 'demo-1'}
            selectedInclusionIds={currentEstimate?.selectedInclusionIds}
            selectedExclusionIds={currentEstimate?.selectedExclusionIds}
            onSelectionChange={(field, ids) => {
              updateEstimateFields({ [field]: ids })
              debouncedSave(field, ids)
            }}
          />
        )}

        {/* Bid Totals Panel — always mounted (so bidTotalComputed stays current on any tab),
            hidden via CSS when not on bid-totals tab. Unit pricing estimates never show it. */}
        {!isUnitPricing && (
        <div style={activeTab !== 'bid-totals' ? { display: 'none' } : undefined}>
          <BidTotalsPanel
            estimateId={id || 'demo-1'}
            subtotal={subtotal}
            lineItems={lineItems}
            initialLchData={lchRows}
            scopeOfWork={currentEstimate?.scopeOfWork || ''}
            onScopeOfWorkChange={(val) => {
              updateEstimateFields({ scopeOfWork: val })
              debouncedSave('scopeOfWork', val)
            }}
            discountType={discountType}
            discountValue={discountValue}
            showDiscountOnProposal={showDiscountOnProposal}
            onDiscountTypeChange={(type) => {
              updateEstimateFields({ discountType: type })
              debouncedSave('discountType', type)
            }}
            onDiscountValueChange={(val) => {
              updateEstimateFields({ discountValue: val })
              debouncedSave('discountValue', val)
            }}
            onShowDiscountOnProposalChange={(show) => {
              updateEstimateFields({ showDiscountOnProposal: show })
              debouncedSave('showDiscountOnProposal', show)
            }}
            projectConditions={currentEstimate?.projectConditions || ''}
            additionalNotes={currentEstimate?.additionalNotes || ''}
            onProjectConditionsChange={(val) => {
              updateEstimateFields({ projectConditions: val })
              debouncedSave('projectConditions', val)
            }}
            onAdditionalNotesChange={(val) => {
              updateEstimateFields({ additionalNotes: val })
              debouncedSave('additionalNotes', val)
            }}
            priceValidThrough={currentEstimate?.priceValidThrough}
            onPriceValidThroughChange={(isoDate) => {
              updateEstimateFields({ priceValidThrough: isoDate })
              debouncedSave('priceValidThrough', isoDate)
            }}
            subcontractorsData={currentEstimate?.subcontractorsData}
            onRemoveLegacySubcontractor={(subId) => {
              const rows = (currentEstimate?.subcontractorsData || []).filter((r) => r.id !== subId)
              updateEstimateFields({ subcontractorsData: rows })
              debouncedSave('subcontractorsData', rows)
            }}
            initialMarkupData={currentEstimate?.markupData}
            flushMarkupRef={bidTotalsFlushRef}
            onMarkupDataChange={(data) => {
              updateEstimateFields({ markupData: data })
              debouncedSave('markupData', data)
            }}
            onComputedTotalChange={(total) => {
              setBidTotalComputed(total)
              // Persist the markup-applied total so the proposal PDF always reflects it.
              // Guard against the initial-load fire with total=0 (no line items yet).
              if (total > 0) debouncedSave('total', total)
            }}
          />
        </div>
        )}

        {/* Assets Panel */}
        {activeTab === 'assets' && (
          <AssetsPanel estimateId={id || 'demo-1'} />
        )}

        {/* Map Panel */}
        {activeTab === 'mapping' && (
          <MapPanel
            estimateId={id || 'demo-1'}
            jobAddress={currentEstimate?.jobAddress}
            jobCity={currentEstimate?.jobCity}
            jobState={currentEstimate?.jobState}
            jobZip={currentEstimate?.jobZip}
            initialMapData={currentEstimate?.mapJson}
            onMapChange={(mapJson, totalSquareFootage) => {
              updateEstimateFields({ mapJson, totalSquareFootage });
            }}
          />
        )}

        {/* Background material sync: creates line items from writeup scope data
            for sections whose tab isn't currently active. Fixes the bug where
            checking writeup rows then going to Bid Totals showed empty totals. */}
        {addedCategories.size > 0 && (
          <BackgroundMaterialSync
            sections={[...addedCategories].filter(s => s !== 'striping' && s !== 'subcontractors' && s !== 'options')}
            scopeData={currentEstimate?.scopeData}
            pricingItems={pricingItems}
            lineItems={lineItems}
            onAddItem={handleAddLineItem}
            onUpdateItem={handleUpdateLineItem}
            onDeleteItem={handleDeleteLineItem}
          />
        )}

        {/* Added Category Panel Content */}
        {addedCategories.has(activeTab as string) && (
          activeTab === 'asphalt' ? (
            <AsphaltGradingPanel
              key="asphalt"
              estimateId={id || 'demo-1'}
              section="asphalt"
              serviceFilter="Asphalt"
              pricingItems={pricingItems}
              lineItems={lineItems}
              isPublicWorks={currentEstimate?.isPublicWorks}
              defaultFuelChargePercent={defaultFuelChargePercent}
              scopeData={currentEstimate?.scopeData}
              initialCosts={lchTenantCosts}
              initialLchData={lchRows}
              onAddItem={handleAddLineItem}
              onUpdateItem={handleUpdateLineItem}
              onDeleteItem={handleDeleteLineItem}
              onWriteupRowDisable={handleWriteupRowDisable}
              onWriteupFieldUpdate={handleWriteupFieldUpdate}
              onLchDataChange={handleLchDataChange}
            />
          ) : activeTab === 'concrete' ? (
            <AsphaltGradingPanel
              key="concrete"
              estimateId={id || 'demo-1'}
              section="concrete"
              serviceFilter="Concrete"
              pricingItems={pricingItems}
              lineItems={lineItems}
              isPublicWorks={currentEstimate?.isPublicWorks}
              defaultFuelChargePercent={defaultFuelChargePercent}
              scopeData={currentEstimate?.scopeData}
              initialCosts={lchTenantCosts}
              initialLchData={lchRows}
              onAddItem={handleAddLineItem}
              onUpdateItem={handleUpdateLineItem}
              onDeleteItem={handleDeleteLineItem}
              onWriteupRowDisable={handleWriteupRowDisable}
              onWriteupFieldUpdate={handleWriteupFieldUpdate}
              onLchDataChange={handleLchDataChange}
            />
          ) : activeTab === 'grading' ? (
            <AsphaltGradingPanel
              key="grading"
              estimateId={id || 'demo-1'}
              section="grading"
              serviceFilter="Grading"
              pricingItems={pricingItems}
              lineItems={lineItems}
              isPublicWorks={currentEstimate?.isPublicWorks}
              defaultFuelChargePercent={defaultFuelChargePercent}
              scopeData={currentEstimate?.scopeData}
              initialCosts={lchTenantCosts}
              initialLchData={lchRows}
              onAddItem={handleAddLineItem}
              onUpdateItem={handleUpdateLineItem}
              onDeleteItem={handleDeleteLineItem}
              onWriteupRowDisable={handleWriteupRowDisable}
              onWriteupFieldUpdate={handleWriteupFieldUpdate}
            />
          ) : activeTab === 'seal-coat' ? (
            <SealCoatGradingPanel
              estimateId={id || 'demo-1'}
              pricingItems={pricingItems}
              lineItems={lineItems}
              isPublicWorks={currentEstimate?.isPublicWorks}
              defaultFuelChargePercent={defaultFuelChargePercent}
              scopeData={currentEstimate?.scopeData}
              onAddItem={handleAddLineItem}
              onUpdateItem={handleUpdateLineItem}
              onDeleteItem={handleDeleteLineItem}
              onWriteupRowDisable={handleWriteupRowDisable}
              onWriteupFieldUpdate={handleWriteupFieldUpdate}
            />
          ) : activeTab === 'striping' ? (
            <StripingEstimatePanel
              estimateId={id || 'demo-1'}
              pricingItems={pricingItems}
              lineItems={lineItems}
              onAddItem={handleAddLineItem}
              onUpdateItem={handleUpdateLineItem}
              onDeleteItem={handleDeleteLineItem}
            />
          ) : activeTab === 'options' ? (
            <OptionsPanel
              estimateId={id || 'demo-1'}
              initialData={currentEstimate?.optionsData}
              onRowsChange={(rows) => updateEstimateFields({ optionsData: rows })}
            />
          ) : (
            <PanelPlaceholder
              icon={getCategoryIcon(activeTab)}
              title={
                ADD_CATEGORY_OPTIONS.find((opt) => opt.id === activeTab)?.label || 'Category'
              }
              description={`Custom category: ${activeTab}`}
            />
          )
        )}

        {/* Subcontractors — the LAST section of every service tab, directly after
            Miscellaneous (each service panel above ends with Miscellaneous). Entries
            are tied to this tab's service automatically; their markup is set in
            Final Bid Totals. All services share one list, owned here, so a tab's
            edits can never overwrite another tab's entries. */}
        {addedCategories.has(activeTab as string) && SERVICE_TABS.has(activeTab as string) && (
          <ServiceSubcontractorsSection
            section={activeTab as string}
            allRows={currentEstimate?.subcontractorsData || []}
            onChange={(rows) => {
              updateEstimateFields({ subcontractorsData: rows })
              debouncedSave('subcontractorsData', rows)
            }}
          />
        )}
      </div>

      {/* Add Line Item Dialog */}
      <AddLineItemDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onAdd={handleAddLineItemFromDialog}
        pricingItems={pricingItems}
      />

      {/* Sticky Footer - on Bid Totals and Unit Price tab */}
      {(activeTab === 'bid-totals' || (activeTab === 'unit-price' && currentEstimate?.isUnitPricing)) && (
      <div className="bt-footer">
        <div className="bt-footer-left">
          <div className="bt-footer-accent"></div>
          <div>
            <div className="bt-footer-label">Final Bid Total</div>
            <div className="bt-footer-amount">
              ${finalTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {discountValue > 0 && (
                <span className="bt-footer-discount-note">
                  {discountType === 'percentage'
                    ? `with ${discountValue}% discount`
                    : `with $${discountValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} discount`}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="bt-footer-right">
          {/* Revision mode: only show the estimator's "Submit Revision for Approval" flow while
              the revision is still a draft. Once status flips to submitted_for_approval, fall
              through to the manager Approve/Reject branch so reviewers can act on the revision
              the same way they do on a first-time estimate. */}
          {currentEstimate?.isRevision && currentEstimate?.revisionProposalId && currentEstimate?.status === 'draft' ? (
            <>
              <button onClick={handlePreviewProposal} className="bt-footer-btn">
                Preview Proposal
              </button>
              <button
                onClick={handleUpdateProposal}
                className={`bt-footer-btn${(isManagerOrAbove || !requireApproval) ? '' : ' bt-footer-btn-update'}`}
                disabled={isUpdatingProposal}
              >
                {isUpdatingProposal ? (
                  <>
                    <span className="prop-send-spinner" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
                      <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
                    </svg>
                    Submit Revision for Approval
                  </>
                )}
              </button>
              {/* Admin/manager (or tenants with requireApproval off) can skip the queue and
                  push the revision straight onto the existing proposal. Backend
                  createProposalDirect auto-approves the estimate, then createProposal detects
                  revisionProposalId and swaps the proposal in place — same idempotent path used
                  on first-time estimates' Create Proposal button. */}
              {(isManagerOrAbove || !requireApproval) && (
                <button
                  onClick={handleCreateProposal}
                  className="bt-footer-btn bt-footer-btn-update"
                  disabled={isCreatingProposal}
                >
                  {isCreatingProposal ? (
                    <>
                      <span className="prop-send-spinner" />
                      Updating...
                    </>
                  ) : (
                    'Update Proposal Now'
                  )}
                </button>
              )}
            </>
          ) : isManagerOrAbove && currentEstimate?.status === 'submitted_for_approval' && !isApproved ? (
            <>
              {approveError && (
                <span className="bt-footer-error">{approveError}</span>
              )}
              <button onClick={handlePreviewProposal} className="bt-footer-btn">
                Preview Proposal
              </button>
              <button
                onClick={handleRejectClick}
                className="bt-footer-btn bt-footer-btn-reject"
              >
                Reject
              </button>
              <button
                onClick={handleApprove}
                className="bt-footer-btn bt-footer-btn-approve"
              >
                Approve
              </button>
            </>
          ) : isApproved || currentEstimate?.status === 'approved' ? (
            /* Approved state — green button */
            <>
              <button onClick={handlePreviewProposal} className="bt-footer-btn">
                Preview Proposal
              </button>
              <button className="bt-footer-btn bt-footer-btn-approved-state" disabled>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
                Approved
              </button>
            </>
          ) : currentEstimate?.status === 'rejected' ? (
            /* Rejected state */
            <button className="bt-footer-btn bt-footer-btn-rejected-state" disabled>
              Rejected
            </button>
          ) : (
            /* Draft/submitted estimate: Create Proposal or Submit for Approval */
            <>
              <button onClick={handlePreviewProposal} className="bt-footer-btn">
                Preview Proposal
              </button>
              {currentEstimate?.status === 'draft' ? (
                (isManagerOrAbove || !requireApproval) ? (
                  <>
                    {submitApprovalError && (
                      <span className="bt-footer-error">Failed to submit — please try again</span>
                    )}
                    <button
                      onClick={handleSubmit}
                      className="bt-footer-btn"
                    >
                      Submit for Approval
                    </button>
                    <button
                      onClick={handleCreateProposal}
                      className="bt-footer-btn bt-footer-btn-submit"
                      disabled={isCreatingProposal}
                    >
                      {isCreatingProposal ? (
                        <>
                          <span className="prop-send-spinner" />
                          Creating...
                        </>
                      ) : (
                        'Create Proposal'
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    {submitApprovalError && (
                      <span className="bt-footer-error">Failed to submit — please try again</span>
                    )}
                    <button
                      onClick={handleSubmit}
                      className="bt-footer-btn bt-footer-btn-submit"
                    >
                      Submit for Approval
                    </button>
                  </>
                )
              ) : currentEstimate?.status === 'submitted_for_approval' ? (
                /* Estimator's view — managers/admins hit the approve/reject
                   branch above and never reach this. */
                <>
                  {returnToDraftError && (
                    <span className="bt-footer-error">Failed to return to draft — please try again</span>
                  )}
                  <button
                    onClick={handleReturnToDraft}
                    className="bt-footer-btn"
                    disabled={isReturningToDraft}
                    title="Pull this estimate back from the approval queue so you can edit it"
                  >
                    {isReturningToDraft ? 'Returning...' : 'Return to Draft'}
                  </button>
                  <button className="bt-footer-btn bt-footer-btn-submitted" disabled>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16" height="16">
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Submitted for Approval
                  </button>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && (
        <div className="est-modal-overlay" onClick={() => setRejectModalOpen(false)}>
          <div className="est-modal" onClick={(e) => e.stopPropagation()}>
            <div className="est-modal-header">
              <h3 className="est-modal-title">Reject Estimate</h3>
              <button className="est-modal-close" onClick={() => setRejectModalOpen(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="est-modal-body">
              <label className="est-modal-label">Reason for rejection</label>
              <textarea
                className="est-modal-textarea"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Leave a note explaining why this estimate is being rejected..."
                rows={4}
                autoFocus
              />
            </div>
            <div className="est-modal-footer">
              <button className="est-modal-btn-cancel" onClick={() => setRejectModalOpen(false)}>
                Cancel
              </button>
              <button
                className="est-modal-btn-reject"
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim()}
              >
                Reject Estimate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Details Modal */}
      <EditProjectDetailsModal
        open={editDetailsOpen}
        onClose={() => setEditDetailsOpen(false)}
        estimate={currentEstimate}
        onSaved={(updated) => setEstimate(updated)}
      />

      {/* Field Notes Drawer */}
      <FieldNotesPanel
        estimateId={id!}
        open={notesDrawerOpen}
        onClose={() => setNotesDrawerOpen(false)}
        projectName={currentEstimate.title}
      />

    </div>
  )
}

/**
 * Panel Placeholder Component - shows for sections not yet fully built
 */
function PanelPlaceholder({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="card">
      <div className="est-panel-placeholder">
        <div className="est-panel-placeholder-icon">{icon}</div>
        <h3 className="est-panel-placeholder-title">{title}</h3>
        <p className="est-panel-placeholder-desc">{description}</p>
      </div>
    </div>
  )
}

/**
 * Get icon for added category
 */
function getCategoryIcon(_section: string) {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="w-12 h-12">
      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}


